/**
 * Оболочка приложения: шапка, левая панель, область содержимого.
 *
 * Бюджет хрома — 15 % высоты экрана (docs/04, §1.2): шапка 44 px и панель
 * 224 px это почти всё, что интерфейс имеет право забрать у данных.
 *
 * Командная палитра — не украшение, а основной способ навигации: переход
 * к задаче по номеру, поиск и создание делаются с клавиатуры.
 */

import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Bell,
  BarChart3,
  CalendarRange,
  FolderKanban,
  GanttChartSquare,
  LayoutList,
  ListPlus,
  LogOut,
  Moon,
  Search,
  LayoutGrid,
  Settings,
  ShieldCheck,
  Users,
  Palette as PaletteIcon,
} from "lucide-react";

import { TimerWidget } from "@/modules/analytics/TimerWidget";
import { SecurityDialog } from "@/modules/auth/SecurityDialog";
import { ProjectTree } from "@/modules/projects/ProjectTree";
import { HelpButton } from "@/shared/ui/Help";
import * as work from "@/shared/api/work";
import * as workspaces from "@/shared/api/workspaces";
import { cn } from "@/shared/lib/cn";
import { useSession } from "@/shared/lib/session";
import { getTheme, setTheme } from "@/shared/lib/theme";
import { Avatar } from "@/shared/ui/Avatar";
import { Badge } from "@/shared/ui/Badge";
import { Button } from "@/shared/ui/Button";
import { CommandPalette, useCommandPalette, type Command } from "@/shared/ui/CommandPalette";
import { Kbd } from "@/shared/ui/Feedback";
import { Popover } from "@/shared/ui/Overlays";
import { DropdownMenu } from "@/shared/ui/Overlays";
import { Palette as ColorPalette } from "@/shared/ui/Palette";
import { DensityToggle, ThemeToggle } from "@/shared/ui/Toggles";

interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  to?: string;
}

/** Значок и адрес известного раздела: подпись может переопределить пространство. */
const SECTION_ICONS: Record<string, React.ReactNode> = {
  tasks: <LayoutList className="size-[var(--pt-icon)]" />,
  planner: <FolderKanban className="size-[var(--pt-icon)]" />,
  calendar: <CalendarRange className="size-[var(--pt-icon)]" />,
  gantt: <GanttChartSquare className="size-[var(--pt-icon)]" />,
  reports: <BarChart3 className="size-[var(--pt-icon)]" />,
  contacts: <Users className="size-[var(--pt-icon)]" />,
  settings: <Settings className="size-[var(--pt-icon)]" />,
};

const SECTION_ROUTES: Record<string, string> = {
  tasks: "/tasks",
  planner: "/planner",
  calendar: "/calendar",
  gantt: "/gantt",
  reports: "/reports",
  contacts: "/contacts",
  settings: "/settings",
};

/** Разбор адреса пункта: путь и параметры роутер принимает по отдельности. */
function toTarget(target: string): { to: string; search: Record<string, unknown> } {
  const [path, query] = target.split("?");
  const search: Record<string, unknown> = {};
  for (const [key, value] of new URLSearchParams(query ?? "")) {
    if (value === "true" || value === "false") {
      search[key] = value === "true";
    } else if (value !== "" && !Number.isNaN(Number(value))) {
      search[key] = Number(value);
    } else {
      search[key] = value;
    }
  }
  return { to: path ?? target, search };
}

/** Пункты меню рабочего пространства в том, что умеет показать оболочка.
 *
 * Настройка отсюда выбрасывается: она живёт в шапке шестерёнкой, и второй
 * такой же пункт в списке разделов только сбивал бы с толку. */
function toNav(items: workspaces.MenuItem[]): NavItem[] {
  return items
    .filter((item) => item.key !== "settings" && item.to !== "/settings")
    .map((item) => ({
      key: item.key,
      label: item.label ?? item.key,
      icon: SECTION_ICONS[item.key] ?? <ListPlus className="size-[var(--pt-icon)]" />,
      ...((item.to ?? SECTION_ROUTES[item.key]) ? { to: item.to ?? SECTION_ROUTES[item.key] } : {}),
    }));
}

const NAV: NavItem[] = [
  {
    key: "tasks",
    label: "Задачи",
    icon: <LayoutList className="size-[var(--pt-icon)]" />,
    to: "/tasks",
  },
  {
    key: "planner",
    label: "Планировщик",
    icon: <FolderKanban className="size-[var(--pt-icon)]" />,
    to: "/planner",
  },
  {
    key: "calendar",
    label: "Календарь",
    icon: <CalendarRange className="size-[var(--pt-icon)]" />,
    to: "/calendar",
  },
  {
    key: "gantt",
    label: "Гантт",
    icon: <GanttChartSquare className="size-[var(--pt-icon)]" />,
    to: "/gantt",
  },
  {
    key: "reports",
    label: "Отчёты",
    icon: <BarChart3 className="size-[var(--pt-icon)]" />,
    to: "/reports",
  },
  {
    key: "contacts",
    label: "Контакты",
    icon: <Users className="size-[var(--pt-icon)]" />,
    to: "/contacts",
  },
];

// Настройка живёт в шапке, а не в списке разделов: в неё заходят изредка,
// а место в левом меню занимает наравне с ежедневными задачами

const SEARCH_DEBOUNCE_MS = 200;

/** Заголовок уведомления: в payload лежит произвольный JSON, а не только строки. */
function notificationLabel(item: work.Notification): string {
  const name = item.payload["task_name"];
  return typeof name === "string" && name ? name : item.type;
}

export interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps): React.ReactElement {
  const user = useSession((state) => state.user);
  const signOut = useSession((state) => state.signOut);
  const navigate = useNavigate();
  // Справка выбирает тему по адресу: адрес здесь и есть состояние экрана
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [section, setSection] = React.useState("tasks");
  const [workspaceCode, setWorkspaceCode] = React.useState<string | null>(
    workspaces.rememberedWorkspace(),
  );
  const spaces = useQuery({ queryKey: ["workspaces-mine"], queryFn: workspaces.myWorkspaces });
  const available = spaces.data ?? [];
  const current =
    available.find((item) => item.code === workspaceCode) ??
    available.find((item) => item.is_default) ??
    available[0];
  // Пока пространства не пришли, меню остаётся прежним: пустая панель на
  // полсекунды выглядит как поломка
  const nav = current === undefined ? NAV : toNav(current.menu);

  const [project, setProject] = React.useState<number | null>(null);
  const [securityOpen, setSecurityOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const palette = useCommandPalette();

  const notifications = useQuery({
    queryKey: ["notifications"],
    queryFn: () => work.inbox(),
    // Колокольчик обновляется сам: уведомление приходит, пока человек работает
    refetchInterval: 60_000,
  });

  // Поиск в палитре: запрос уходит не на каждую букву
  const [debounced, setDebounced] = React.useState("");
  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  const found = useQuery({
    queryKey: ["palette-search", debounced],
    queryFn: () => work.search(debounced, 6),
    enabled: palette.open && debounced.length >= 2,
  });

  const dynamic: Command[] = React.useMemo(() => {
    const commands: Command[] = [];

    // Номер задачи — самый частый способ перехода: его называют вслух
    const asNumber = Number(debounced.replace("#", ""));
    if (Number.isInteger(asNumber) && asNumber > 0) {
      commands.push({
        id: `task-number-${asNumber}`,
        title: `Открыть задачу №${asNumber}`,
        group: "Переходы",
        run: () => {
          void work
            .search(String(asNumber), 5)
            .then((results) => results.tasks.find((task) => task.number === asNumber))
            .then((task) => {
              if (task) {
                void navigate({ to: "/tasks/$taskId", params: { taskId: String(task.id) } });
              }
            });
        },
      });
    }

    for (const task of found.data?.tasks ?? []) {
      commands.push({
        id: `task-${task.id}`,
        title: task.name,
        hint: `#${task.number}`,
        group: "Задачи",
        run: () => void navigate({ to: "/tasks/$taskId", params: { taskId: String(task.id) } }),
      });
    }

    for (const hit of found.data?.comments ?? []) {
      commands.push({
        id: `comment-${hit.comment_id}`,
        title: hit.headline.replace(/<\/?mark>/g, ""),
        hint: "запись ленты",
        group: "Лента",
        run: () => void navigate({ to: "/tasks/$taskId", params: { taskId: String(hit.task_id) } }),
      });
    }

    return commands;
  }, [debounced, found.data, navigate]);

  const commands: Command[] = React.useMemo(
    () => [
      ...NAV.filter((item) => item.to).map((item) => ({
        id: `go-${item.key}`,
        title: `Перейти: ${item.label}`,
        group: "Переходы",
        icon: item.icon,
        run: () => {
          setSection(item.key);
          const target = toTarget(item.to ?? "/tasks");
          void navigate({ to: target.to as "/tasks", search: target.search });
        },
      })),
      {
        id: "new-task",
        title: "Создать задачу",
        group: "Создание",
        icon: <ListPlus className="size-[var(--pt-icon)]" />,
        run: () => void navigate({ to: "/tasks", search: {} }),
      },
      {
        id: "overdue",
        title: "Показать просроченные",
        group: "Вид",
        run: () => void navigate({ to: "/tasks", search: { overdue: true } }),
      },
      {
        id: "unread",
        title: "Показать непрочитанные",
        group: "Вид",
        run: () => void navigate({ to: "/tasks", search: { unread: true } }),
      },
      {
        id: "theme",
        title: "Сменить тему",
        group: "Вид",
        icon: <Moon className="size-[var(--pt-icon)]" />,
        run: () => setTheme(getTheme() === "dark" ? "light" : "dark"),
      },
      {
        id: "companies",
        title: "Настройка: компании",
        group: "Переходы",
        icon: <Settings className="size-[var(--pt-icon)]" />,
        run: () => void navigate({ to: "/settings" }),
      },
      {
        id: "security",
        title: "Безопасность учётной записи",
        group: "Учётная запись",
        icon: <ShieldCheck className="size-[var(--pt-icon)]" />,
        run: () => setSecurityOpen(true),
      },
    ],
    [navigate],
  );

  return (
    <div className="flex h-screen flex-col bg-[var(--pt-bg-canvas)] text-[var(--pt-text-primary)]">
      {/* data-chrome помечает обвязку: оформление «Рабочий стол» переопределяет
          на этом поддереве значения токенов, и всё, что стоит внутри, —
          переключатели, часы, дерево проектов — перекрашивается само.
          Шапка и панель — разные области: их можно покрасить по-разному,
          и надпись на каждой считается по её собственному фону */}
      <header
        data-chrome="bar"
        className="flex h-[var(--pt-header-h)] shrink-0 items-center gap-[var(--pt-space-12)] border-b border-[var(--pt-border-default)] bg-[var(--pt-bg-surface)] px-[var(--pt-space-12)]"
      >
        <span className="text-[var(--pt-fs-md)] font-semibold">PlanTask</span>

        <button
          type="button"
          onClick={() => palette.setOpen(true)}
          className={cn(
            // Ширина — предел, а не требование: на узком экране поле сжимается,
            // иначе оно выдавливает соседей за край шапки
            "ml-[var(--pt-space-16)] flex h-[var(--pt-control-h-md)] w-[360px] min-w-[120px] shrink",
            "items-center gap-[var(--pt-space-8)]",
            "rounded-[var(--pt-radius-3)] border border-transparent bg-[var(--pt-bg-sunken)]",
            "px-[var(--pt-space-8)] text-left text-[var(--pt-font-ui)] text-[var(--pt-text-tertiary)]",
            "hover:border-[var(--pt-border-control)] focus-visible:outline-none focus-visible:shadow-[var(--pt-ring)]",
          )}
        >
          <Search aria-hidden className="size-[var(--pt-icon)]" />
          <span className="min-w-0 flex-1 truncate">Поиск задач, записей и файлов</span>
          <span className="shrink-0">
            <Kbd keys={["Ctrl", "K"]} />
          </span>
        </button>

        {available.length > 1 && (
          <DropdownMenu
            align="start"
            trigger={
              <button
                type="button"
                aria-label="Рабочее пространство"
                className={cn(
                  "flex h-[var(--pt-control-h-md)] items-center gap-[var(--pt-space-6)]",
                  "rounded-[var(--pt-radius-3)] px-[var(--pt-space-8)]",
                  "text-[var(--pt-font-ui)] hover:bg-[var(--pt-bg-hover)]",
                  "focus-visible:outline-none focus-visible:shadow-[var(--pt-ring)]",
                )}
              >
                <LayoutGrid className="size-[var(--pt-icon)]" />
                {current?.name ?? "Пространство"}
              </button>
            }
            items={available.map((item) => ({
              key: item.code,
              label: item.name,
              onSelect: () => {
                setWorkspaceCode(item.code);
                workspaces.rememberWorkspace(item.code);
                const start = item.menu[0];
                const address = start?.to ?? SECTION_ROUTES[start?.key ?? "tasks"];
                if (address !== undefined) {
                  const target = toTarget(address);
                  setSection(start?.key ?? "tasks");
                  void navigate({ to: target.to as "/tasks", search: target.search });
                }
              },
            }))}
          />
        )}

        <div className="ml-auto flex items-center gap-[var(--pt-space-8)]">
          <TimerWidget
            onOpenTask={(taskId) =>
              void navigate({ to: "/tasks/$taskId", params: { taskId: String(taskId) } })
            }
          />
          <DropdownMenu
            align="end"
            trigger={
              <button
                type="button"
                aria-label={`Уведомления: непрочитанных ${notifications.data?.unread ?? 0}`}
                className="relative rounded-[var(--pt-radius-3)] p-[var(--pt-space-4)] hover:bg-[var(--pt-bg-hover)] focus-visible:outline-none focus-visible:shadow-[var(--pt-ring)]"
              >
                <Bell className="size-[var(--pt-icon-lg)]" />
                {(notifications.data?.unread ?? 0) > 0 && (
                  <span className="absolute right-[2px] top-[2px] size-[8px] rounded-full bg-[var(--pt-danger)]" />
                )}
              </button>
            }
            items={[
              ...(notifications.data?.items ?? []).slice(0, 8).map((item) => ({
                key: String(item.id),
                label: notificationLabel(item),
                onSelect: () => {
                  if (item.task_id !== null) {
                    void navigate({
                      to: "/tasks/$taskId",
                      params: { taskId: String(item.task_id) },
                    });
                  }
                },
              })),
              {
                key: "read-all",
                label: "Отметить все прочитанными",
                separatorBefore: true,
                onSelect: () => {
                  void work.markNotificationsRead().then(() => notifications.refetch());
                },
              },
            ]}
          />
          <HelpButton pathname={pathname} />
          <button
            type="button"
            aria-label="Настройка"
            title="Настройка"
            onClick={() => void navigate({ to: "/settings" })}
            className="rounded-[var(--pt-radius-3)] p-[var(--pt-space-4)] hover:bg-[var(--pt-bg-hover)] focus-visible:outline-none focus-visible:shadow-[var(--pt-ring)]"
          >
            <Settings className="size-[var(--pt-icon-lg)]" />
          </button>
          <ThemeToggle />
          <DensityToggle />
          <Popover
            align="end"
            trigger={
              <button
                type="button"
                aria-label="Цвета оформления"
                title="Цвета оформления"
                className="rounded-[var(--pt-radius-3)] p-[var(--pt-space-4)] hover:bg-[var(--pt-bg-hover)] focus-visible:outline-none focus-visible:shadow-[var(--pt-ring)]"
              >
                <PaletteIcon className="size-[var(--pt-icon-lg)]" />
              </button>
            }
          >
            <ColorPalette />
          </Popover>
          {user && (
            <DropdownMenu
              align="end"
              trigger={
                <button
                  type="button"
                  aria-label="Меню пользователя"
                  className="rounded-[var(--pt-radius-full)] focus-visible:outline-none focus-visible:shadow-[var(--pt-ring)]"
                >
                  <Avatar name={user.full_name} />
                </button>
              }
              items={[
                { key: "profile", label: user.full_name, disabled: true },
                {
                  key: "security",
                  label: "Безопасность",
                  icon: <ShieldCheck className="size-[var(--pt-icon)]" />,
                  onSelect: () => setSecurityOpen(true),
                },
                {
                  key: "logout",
                  label: "Выйти",
                  icon: <LogOut className="size-[var(--pt-icon)]" />,
                  separatorBefore: true,
                  onSelect: () => void signOut(),
                },
              ]}
            />
          )}
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <nav
          data-chrome="rail"
          aria-label="Разделы"
          className="flex w-[var(--pt-nav-w)] shrink-0 flex-col gap-[2px] overflow-hidden border-r border-[var(--pt-border-default)] bg-[var(--pt-bg-rail)] p-[var(--pt-space-8)]"
        >
          {nav.map((item) => (
            <button
              key={item.key}
              type="button"
              aria-current={section === item.key ? "page" : undefined}
              disabled={!item.to}
              onClick={() => {
                setSection(item.key);
                if (item.to) {
                  const target = toTarget(item.to);
                  void navigate({ to: target.to as "/tasks", search: target.search });
                }
              }}
              className={cn(
                "flex h-[var(--pt-row-h)] items-center gap-[var(--pt-space-8)] rounded-[var(--pt-radius-3)]",
                "px-[var(--pt-space-8)] text-[var(--pt-font-ui)] transition-colors duration-[var(--pt-dur-micro)]",
                "focus-visible:outline-none focus-visible:shadow-[var(--pt-ring-inset)]",
                "disabled:cursor-not-allowed disabled:text-[var(--pt-text-disabled)]",
                section === item.key && item.to
                  ? "bg-[var(--pt-bg-selected)] text-[var(--pt-text-primary)]"
                  : "text-[var(--pt-text-secondary)] hover:bg-[var(--pt-bg-hover)] hover:text-[var(--pt-text-primary)]",
              )}
            >
              <span className="text-[var(--pt-text-tertiary)]">{item.icon}</span>
              {item.label}
              {!item.to && (
                <Badge tone="neutral" className="ml-auto">
                  скоро
                </Badge>
              )}
            </button>
          ))}

          {/* flex + min-h-0: дерево должно получить остаток высоты полосы,
              а не высоту своего содержимого — иначе шестьдесят проектов
              просто уезжают за нижний край */}
          <div className="mt-[var(--pt-space-8)] flex min-h-0 flex-1 flex-col border-t border-[var(--pt-border-subtle)]">
            <ProjectTree
              selectedId={project}
              onOpen={(projectId) => {
                setProject(projectId);
                setSection("tasks");
                void navigate({
                  to: "/projects/$projectId",
                  params: { projectId: String(projectId) },
                });
              }}
              onSelect={(projectId) => {
                setProject(projectId);
                setSection("tasks");
                void navigate({
                  to: "/tasks",
                  search: projectId === null ? {} : { project: projectId },
                });
              }}
            />
          </div>
        </nav>

        <main className="min-w-0 flex-1 overflow-hidden p-[var(--pt-gutter)]">{children}</main>
      </div>

      <CommandPalette
        commands={commands}
        dynamic={dynamic}
        open={palette.open}
        onOpenChange={palette.setOpen}
        onQueryChange={setQuery}
      />

      {user && <SecurityDialog open={securityOpen} onOpenChange={setSecurityOpen} user={user} />}
    </div>
  );
}

export function SectionPlaceholder({ title }: { title: string }): React.ReactElement {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <p className="text-[var(--pt-fs-lg)] font-medium">{title}</p>
        <p className="mt-[var(--pt-space-4)] text-[var(--pt-fs-sm)] text-[var(--pt-text-secondary)]">
          Раздел появится в следующих фазах.
        </p>
        <Button variant="secondary" className="mt-[var(--pt-space-16)]" disabled>
          Создать задачу
        </Button>
      </div>
    </div>
  );
}
